import React, { useCallback, useEffect, useState, useRef } from 'react';
import { List, message, Skeleton, Empty, Modal } from 'antd';
import CustomPagination from '../../components/common/CustomPagination';
import { useTranslation } from 'react-i18next';
import { PlayCircleOutlined, CreditCardOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import httpClient from '../../utils/HttpClient';
import { objectToQuery, isDateExpiredUTC } from '../../utils/Utils';
import { debounce } from 'lodash';
import { COURSE_PURCHASES_URL } from '../../constants/Url';
import { setPurchasedCourseListAction } from '../../modules/dataWebsite/actions';

const formatDuration = (seconds) => {
  if (!seconds) return '';
  if (typeof seconds === 'string' && seconds.includes(':')) {
    return seconds;
  }
  const secs = parseInt(seconds, 10);
  if (isNaN(secs)) return '';
  const hours = Math.floor(secs / 3600);
  const minutes = Math.floor((secs % 3600) / 60);
  const remainingSeconds = secs % 60;

  const paddedMinutes = String(minutes).padStart(2, '0');
  const paddedSeconds = String(remainingSeconds).padStart(2, '0');

  if (hours > 0) {
    return `${hours}:${paddedMinutes}:${paddedSeconds}`;
  }
  return `${minutes}:${paddedSeconds}`;
};

// Outlined badge style matching mobile app (pic 2)
const statusStyle = {
  active:  "border border-green-500 text-green-600 bg-transparent",
  pending: "border border-yellow-500 text-yellow-600 bg-transparent",
  cancel:  "border border-red-400 text-red-500 bg-transparent",
  expired: "border border-red-400 text-red-500 bg-transparent",
};

/** Returns true when the purchase's expiry timestamp is in the past in UTC */
const isExpiredByDate = (expiryTS) => {
  if (!expiryTS) return false;
  return isDateExpiredUTC(expiryTS);
};

export default function PurchasedCoursePage({ isHide }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const purchases = useSelector((state) => state.dataWebsiteReducer?.purchasedCourseData);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(8);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [activeVideoUrl, setActiveVideoUrl] = useState('');
  const [payingId, setPayingId] = useState(null);

  const loadinFirst = useRef(false);

  const fetchData = async () => {
    try {
      if (!loadinFirst?.current && purchases?.length > 0) {
          setLoading(false);
      } else {
          setLoading(true);
      }
      loadinFirst.current = true;
      const query = {
        keyword: '',
        pageNo: pageNo,
        pageSize: pageSize
      };
      const res = await httpClient.get(objectToQuery(COURSE_PURCHASES_URL, query))
        .then(res => res.data)
        .catch(error => ({ status: 500, message: error?.message }));

      if (res.status === 200) {
        setPurchasedCourseListAction(res.data || []);
        setTotal(res.total || res.data?.length || 0);
      } else {
        setPurchasedCourseListAction([]);
        setTotal(0);
      }
    } catch (error) {
      console.error("fetchData -> error", error);
    } finally {
      setLoading(false);
    }
  };

  const debouncedFetchData = useCallback(
    debounce(fetchData, 300),
    [pageNo, pageSize]
  );

  useEffect(() => {
    debouncedFetchData();
    return debouncedFetchData.cancel;
  }, [debouncedFetchData]);

  const handlePay = async (item) => {
    try {
      setPayingId(item.purchaseId);
      // PUT API to simulate updating payment status
      const payload = {
        transactionId: "TX" + Date.now(),
        purchaseId: item.purchaseId,
        paymentStatus: "paid",
        memo: "Paid via Alipay on Web Website"
      };
      
      const res = await httpClient.put(COURSE_PURCHASES_URL, payload)
        .then(res => res.data)
        .catch(error => ({ status: 500, message: error?.message }));

      if (res.status === 200) {
        message.success(t("text_payment_success"));
        fetchData();
      } else {
        message.error(res.message || t("text_payment_failed"));
      }
    } catch (error) {
      console.error("Payment error", error);
      message.error(t("text_payment_error"));
    } finally {
      setPayingId(null);
    }
  };

  const handleCardClick = (coursePurchase, purchaseRecord) => {
    const id = coursePurchase?.courseNo || coursePurchase?.courseId || coursePurchase?._id || coursePurchase?.id 
      || purchaseRecord?.courseNo || purchaseRecord?.courseId || purchaseRecord?._id || purchaseRecord?.id;
    if (id) {
      navigate(`/online-class-member/${id}`);
    }
  };

  return (
    <div className={isHide ? "w-full pb-8" : "esi-std-container w-full min-h-screen pb-16 px-4 md:px-0"}>
      {!isHide && (
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b border-gray-100 pb-3 mt-6 mb-8 gap-4">
          <h1 className="text-[24px] font-bold text-[#111] m-0">{t("text_purchased_course")}</h1>
        </div>
      )}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-white rounded-lg border border-gray-100 p-4 flex flex-col sm:flex-row gap-4 h-full">
              <div className="w-full sm:w-[180px] aspect-[16/9] shrink-0 bg-gray-50 rounded-md overflow-hidden flex items-center justify-center">
                <Skeleton.Image className="!w-full !h-full" active />
              </div>
              <div className="flex flex-col flex-1 justify-between py-1">
                <div>
                  <Skeleton.Input active size="small" style={{ width: '40%', marginBottom: '12px' }} />
                  <Skeleton.Input active size="default" style={{ width: '90%', marginBottom: '8px' }} />
                  <Skeleton.Input active size="small" style={{ width: '60%' }} />
                </div>
                {/* <div className="flex justify-between items-end mt-4 pt-3 border-t border-gray-50">
                  <Skeleton.Input active size="small" style={{ width: '60px' }} />
                  <Skeleton.Button active size="small" style={{ width: '70px', borderRadius: '4px' }} />
                </div> */}
              </div>
            </div>
          ))}
        </div>
      ) : purchases.length > 0 ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {purchases.map((item) => {
              const isPaid = item?.coursePurchase?.purchaseStatus === 'paid';
              return (
                <div 
                  key={item.purchaseId}
                  onClick={() => handleCardClick(item?.coursePurchase, item)}
                  className="bg-white rounded-lg overflow-hidden border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col sm:flex-row h-full p-4 gap-4 cursor-pointer"
                >
                  {/* Thumbnail on Left */}
                  <div 
                    className="w-full sm:w-[180px] aspect-[16/9] overflow-hidden relative rounded-md shrink-0 bg-gray-50 flex items-center justify-center"
                  >
                    <img 
                      src={item?.coursePurchase?.coursePicUrl || ''} 
                      alt={item?.coursePurchase?.courseTitle || ''} 
                      className="w-full h-full object-cover" 
                    />
                    {isPaid ? (
                      <div className="absolute inset-0 bg-black/25 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-300">
                        <PlayCircleOutlined className="text-white text-3xl" />
                      </div>
                    ) : (
                      <div className="absolute inset-0 bg-black/45 flex items-center justify-center">
                        <PlayCircleOutlined className="text-white text-3xl" />
                      </div>
                    )}
                    {item?.coursePurchase?.courseVideoName && (
                      <div className="absolute bottom-1 right-1 bg-black/75 px-1.5 py-0.5 rounded text-[9px] text-white font-medium">
                        {formatDuration(item.coursePurchase.courseVideoName)}
                      </div>
                    )}
                  </div>

                  {/* Details on Right */}
                  <div className="flex flex-col flex-1 text-left justify-between py-1 min-w-0">
                    <div>
                      <div className="flex justify-between items-start gap-2 mb-1">
                        <h4 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCardClick(item?.coursePurchase, item);
                          }}
                          className="text-[14px] sm:text-base font-bold text-gray-950 leading-snug cursor-pointer hover:text-[#637cfb] transition-colors m-0 line-clamp-1 flex-1"
                        >
                          {item?.coursePurchase?.courseTitle || ""}
                        </h4>
                        {(() => {
                          // Real-time expiry check: if date passed → show expired regardless of purchaseStatus
                          const expired = isExpiredByDate(item?.coursePurchase?.expiryTS);
                          const rawStatus = item?.coursePurchase?.purchaseStatus?.toLowerCase();
                          const resolvedStatus = expired ? 'expired' : (rawStatus || 'expired');
                          const styleClass = statusStyle[resolvedStatus] || statusStyle.expired;
                          const labelKey = expired ? 'text_expired' : rawStatus;
                          return (
                            <div className={`px-2 py-0.5 rounded text-[11px] font-semibold capitalize shrink-0 ${styleClass}`}>
                              {t(labelKey || 'text_expired')}
                            </div>
                          );
                        })()}
                      </div>
                      <div className="text-[11px] text-gray-400 truncate">
                        {t("text_valid_time")}: {new Date(item?.coursePurchase?.expiryTS).toLocaleString()}
                      </div>
                    </div>

                    {/* <div className="flex justify-between items-end mt-4 pt-3 border-t border-gray-50">
                      <div>
                        <span className="text-[11px] text-gray-400 block leading-tight">{t("价格")}</span>
                        <span className="text-base font-black text-gray-950">&yen; {item.purchasePrice || item.coursePrice || 0}</span>
                      </div>
                      {!isPaid && (
                        <Button 
                          type="primary" 
                          size="small"
                          loading={payingId === item.purchaseId}
                          onClick={() => handlePay(item)}
                          className="bg-[#21163e] hover:bg-[#252554] border-none text-[12px] font-semibold text-white px-4 rounded-md"
                        >
                          {t("text_pay_now")}
                        </Button>
                      )}
                    </div> */}
                  </div>
                </div>
              );
            })}
          </div>

        </>
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('text_no_purchased_course')} />
      )}

      {(loading || total > 0 || purchases.length > 0) && (
        <div className="flex justify-end mt-8">
          <CustomPagination
            current={pageNo}
            pageSize={pageSize}
            total={loading ? 8 : (total || purchases.length || 0)}
            onChange={(page, limit) => {
              if (limit !== pageSize) {
                setPageSize(limit);
                setPageNo(1);
              } else {
                setPageNo(page);
              }
            }}
          />
        </div>
      )}

      {/* Video Modal */}
      <Modal
        open={isVideoModalOpen}
        onCancel={() => setIsVideoModalOpen(false)}
        footer={null}
        destroyOnClose
        width={800}
        styles={{ body: { padding: 0, backgroundColor: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' } }}
        centered
      >
        <video 
          className="w-full aspect-video rounded-lg" 
          controls 
          controlsList="nodownload"
          onContextMenu={(e) => e.preventDefault()}
          autoPlay 
          src={activeVideoUrl}
        >
          {t("text_tag_not_support")}
        </video>
      </Modal>

    </div>
  );
}
